import { useEffect } from 'react'
import { useCurrencyStore } from '@/stores/currencyStore'

/**
 * Custom hook to manage theme state and apply dark mode classes
 */
export const useTheme = () => {
  const { preferences, updatePreferences } = useCurrencyStore()

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement
    
    // Remove existing theme classes
    root.classList.remove('light', 'dark')
    
    // Add current theme class
    if (preferences.theme === 'auto') {
      // Use system preference
      const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
      root.classList.add(isDarkMode ? 'dark' : 'light')
    } else {
      root.classList.add(preferences.theme)
    }
  }, [preferences.theme])

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (preferences.theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      
      const handleChange = (e: MediaQueryListEvent) => {
        const root = document.documentElement
        root.classList.remove('light', 'dark')
        root.classList.add(e.matches ? 'dark' : 'light')
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [preferences.theme])

  const toggleTheme = () => {
    const newTheme = preferences.theme === 'dark' ? 'light' : 'dark'
    updatePreferences({ theme: newTheme })
  }

  const setTheme = (theme: 'light' | 'dark' | 'auto') => {
    updatePreferences({ theme })
  }

  return {
    theme: preferences.theme,
    isDark: preferences.theme === 'dark' || 
           (preferences.theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches),
    toggleTheme,
    setTheme
  }
}